import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { type Editor } from '@tiptap/react';
import { type PropsWithChildren, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import RichTextField from '@/components/RichTextField';
import RichTextFieldReadOnly from '@/components/RichTextFieldReadOnly';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';

interface GetMarketPositionMetric {
  companyMarketPositionByDeal: {
    __typename: 'CompanyMarketPositionNode';
    barriersToEntryDescription: null | string;
    businessCriticalityCommentary: null | string;
    competitiveAdvantageDescription: null | string;
    id: string;
    marketLeaderDescription: null | string;
    name: null | string;
  };
}

const GET_MARKET_POSITION_METRIC = gql`
  query MarketPositionMetric($deal_id: ID!) {
    companyMarketPositionByDeal(dealOpportunityId: $deal_id) {
      id
      name
      businessCriticalityCommentary
      barriersToEntryDescription
      competitiveAdvantageDescription
      marketLeaderDescription
    }
  }
`;

const UPDATE_COMMENTARIES = gql`
  mutation UpdateCommentaries(
    $id: ID!
    $business_criticality_commentary: String
    $barriers_to_entry_description: String
    $competitive_advantage_description: String
    $market_leader_description: String
  ) {
    updateCompanyMarketPosition(
      id: $id
      values: {
        businessCriticalityCommentary: $business_criticality_commentary
        barriersToEntryDescription: $barriers_to_entry_description
        competitiveAdvantageDescription: $competitive_advantage_description
        marketLeaderDescription: $market_leader_description
      }
    ) {
      ok
    }
  }
`;

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{
  title: string;
}>) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const TextBlock = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-2">
    <div className="text-sm font-medium">{label}</div>
    <RichTextFieldReadOnly html={value} />
  </div>
);

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const InvestmentsDirectIdMarketPosition = () => {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    throw new Error();
  }

  const {
    data: data1,
    error: error1,
    loading: loading1
  } = useQuery<GetMarketPositionMetric, { deal_id: string }>(
    GET_MARKET_POSITION_METRIC,
    {
      variables: { deal_id: id }
    }
  );

  const [updateCommentaries, { loading: isUpdatingCommentaries }] = useMutation(
    UPDATE_COMMENTARIES,
    {
      refetchQueries: [GET_MARKET_POSITION_METRIC]
    }
  );

  const [isOpen, setIsOpen] = useState(false);

  const businessCriticalityFieldRef = useRef<Editor>(null);
  const barriersToEntryFieldRef = useRef<Editor>(null);
  const competitiveAdvantageFieldRef = useRef<Editor>(null);
  const marketLeaderFieldRef = useRef<Editor>(null);

  if (loading1) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (error1) {
    return <div>{'Error'}</div>;
  }

  const handleCommentariesFormSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    if (
      !businessCriticalityFieldRef.current ||
      !barriersToEntryFieldRef.current ||
      !competitiveAdvantageFieldRef.current ||
      !marketLeaderFieldRef.current
    ) {
      toast.error('Unable to read market position commentaries');
      return;
    }

    try {
      const marketPositionId = data1?.companyMarketPositionByDeal.id;
      if (!marketPositionId) {
        toast.error('Market position record not found');
        return;
      }
      const { error } = await updateCommentaries({
        variables: {
          barriers_to_entry_description: barriersToEntryFieldRef.current.isEmpty
            ? null
            : barriersToEntryFieldRef.current.getHTML(),
          business_criticality_commentary: businessCriticalityFieldRef.current
            .isEmpty
            ? null
            : businessCriticalityFieldRef.current.getHTML(),
          competitive_advantage_description: competitiveAdvantageFieldRef
            .current.isEmpty
            ? null
            : competitiveAdvantageFieldRef.current.getHTML(),
          id: marketPositionId,
          market_leader_description: marketLeaderFieldRef.current.isEmpty
            ? null
            : marketLeaderFieldRef.current.getHTML()
        }
      });

      if (error) {
        throw new Error('An error has occurred');
      }

      setIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-7 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="space-y-3">
            <SectionHeader title="Commentaries">
              <Dialog
                onOpenChange={setIsOpen}
                open={isOpen}
              >
                <DialogTrigger asChild>
                  <EditButton />
                </DialogTrigger>
                <DialogContent aria-describedby={undefined}>
                  <DialogHeader>
                    <DialogTitle>
                      Commentaries
                      <Badge variant="yellow">Unsaved</Badge>
                    </DialogTitle>
                  </DialogHeader>
                  <DialogBody>
                    <form
                      id="commentaries-form"
                      noValidate
                      onSubmit={event =>
                        void handleCommentariesFormSubmit(event)
                      }
                    >
                      <FieldGroup>
                        <Field>
                          <FieldLabel htmlFor="business-criticality-field">
                            Business Criticality
                          </FieldLabel>
                          <RichTextField
                            defaultValue={
                              data1?.companyMarketPositionByDeal
                                .businessCriticalityCommentary ?? ''
                            }
                            id="business-criticality-field"
                            ref={businessCriticalityFieldRef}
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="barriers-to-entry-field">
                            Barriers to Entry
                          </FieldLabel>
                          <RichTextField
                            defaultValue={
                              data1?.companyMarketPositionByDeal
                                .barriersToEntryDescription ?? ''
                            }
                            id="barriers-to-entry-field"
                            ref={barriersToEntryFieldRef}
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="competitive-advantages-field">
                            Competitive Advantages
                          </FieldLabel>
                          <RichTextField
                            defaultValue={
                              data1?.companyMarketPositionByDeal
                                .competitiveAdvantageDescription ?? ''
                            }
                            id="competitive-advantages-field"
                            ref={competitiveAdvantageFieldRef}
                          />
                        </Field>
                        <Field>
                          <FieldLabel htmlFor="market-leader-field">
                            Market Leader
                          </FieldLabel>
                          <RichTextField
                            defaultValue={
                              data1?.companyMarketPositionByDeal
                                .marketLeaderDescription ?? ''
                            }
                            id="market-leader-field"
                            ref={marketLeaderFieldRef}
                          />
                        </Field>
                      </FieldGroup>
                    </form>
                  </DialogBody>
                  <DialogFooter>
                    <Button
                      disabled={isUpdatingCommentaries}
                      form="commentaries-form"
                      type="submit"
                    >
                      {isUpdatingCommentaries && <Spinner />}
                      Save
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </SectionHeader>
            <Card>
              <div className="space-y-4">
                <TextBlock
                  label="Business Criticality"
                  value={
                    data1?.companyMarketPositionByDeal
                      .businessCriticalityCommentary ?? ''
                  }
                />
                <TextBlock
                  label="Barriers to Entry"
                  value={
                    data1?.companyMarketPositionByDeal
                      .barriersToEntryDescription ?? ''
                  }
                />
                <TextBlock
                  label="Competitive Advantages"
                  value={
                    data1?.companyMarketPositionByDeal
                      .competitiveAdvantageDescription ?? ''
                  }
                />
                <TextBlock
                  label="Market Leader"
                  value={
                    data1?.companyMarketPositionByDeal
                      .marketLeaderDescription ?? ''
                  }
                />
              </div>
            </Card>
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdMarketPosition;
